<template>
  <div class="product-traits-wrapper margin-top-s">
    <div>
      <ProductPrice
        :id="productId"
        :product="productData"
        :current-zip-code-availability="currentZipCodeAvailabilityData"
        :additional-fee="resolvedAdditionalFee"
        class="margin-bottom-s"
      />
      <div v-if="isMobile" class="badges">
        <slot name="pzo" />
        <slot v-if="isMcom" name="shop-more" />
        <slot v-if="isMcom" name="ppc" />
      </div>
    </div>
    <a
      v-if="memberProductUrl && !isMobile"
      class="link-body margin-bottom-xs"
      :href="memberProductUrl"
      :target="allowOpeningInNewTab ? '_blank' : '_self'"
    >
      View All Product Details
    </a>
    <div v-if="!isMobile" class="badges">
      <slot name="pzo" />
      <slot v-if="isMcom" name="shop-more" />
      <slot v-if="isMcom" name="ppc" />
    </div>
    <ColorSwatches v-if="showColorSwatches && !isConsolidatedThirdVariantFlag" :id="productId" class="margin-bottom-xs" :color-props="colorProps" />
    <ProductSizes :id="productId" :product="productData" />
    <a
      v-if="memberProductUrl && isMobile"
      class="link-body margin-bottom-m"
      :href="memberProductUrl"
      :target="allowOpeningInNewTab ? '_blank' : '_self'"
    >
      View All Product Details
    </a>
    <div v-if="$slots.shopTheSet && isShopTheSetExpEnabled">
      <slot name="shopTheSet" />
    </div>
    <ProductOrientation v-if="isMobile && !isSplitThirdVariantFlag && !isConsolidatedThirdVariantFlag && !isSplitThirdVariantMember" />

    <ProductOrientation v-if="!isMobile && !isSplitThirdVariantFlag && !isConsolidatedThirdVariantFlag && !isSplitThirdVariantMember" />

    <ProtectionPlanBigTicket
      v-if="(memberFeatureEligible?.isQuantityEnabled || isProtectionPlanEligible) && !isMemberOfMasterFlag && !killswitches.isWNMBigTicketEnabled"
    />

    <div
      v-if="(memberFeatureEligible?.isQuantityEnabled || isQuantityEnabled) && !isMemberOfMasterFlag && !isUpdateBagEnabled"
      class="margin-bottom-xs"
    >
      <QuantitySelector v-model="quantity" :min="minQuantity" :max="maxQuantity" size="small" />
    </div>
  </div>
</template>

<script setup>
import QuantitySelector from '@atomic-ui/quantitySelector/QuantitySelector';
import ProductSizes from '~/modules/size/ProductSize';
import ProtectionPlanBigTicket from '~/modules/protection-plan/components/ProtectionPlanBigTicket';
import ProductOrientation from '~/modules/type/components/ProductOrientation.vue';
import ColorSwatches from '~/modules/color/ColorSwatches';
import useColor from '~/modules/color/composables/useColor';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useQuantity } from '~/modules/product/composables/useQuantity';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useBagContext } from '~/modules/add-to-bag/composables/useBagContext';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';

import { useBrand } from '~/composables/useBrand';
import { useProductType } from '~/composables/useProductType';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';

import ProductPrice from '~/modules/price/components';
import { useAvailabilityBigTicket } from '~/modules/availability/composables/useAvailabilityBigTicket';
import { useBigTicketDeliveryMethods } from '~/modules/availability/composables/useBigTicketDeliveryMethods';

const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();
const { isLatchKey } = useProductType();
const { isUpdateBagEnabled } = useBagContext();
const { killswitches } = useKillswitches();

defineProps({
  memberProductUrl: {
    type: String,
    required: false,
    default() {
      return null;
    },
  },
  allowOpeningInNewTab: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const consolidatedStore = useConsolidatedMemberStore();
const { memberFlags, memberFeatureEligible } = storeToRefs(consolidatedStore);
const { hasColorSwatchesFlag, hasColorsFlag, isMemberOfMasterFlag, isSplitThirdVariantFlag, isConsolidatedThirdVariantFlag } = useProductFlags();

const { productData, productId: mainProductId } = useProduct();
const selectedMemberId = useSelectedMemberId();
const { isQuantityEnabled, isProtectionPlanEligible, isShopTheSetExpEnabled } = useFeatureEligibility();
const productId = isShopTheSetExpEnabled ? selectedMemberId : mainProductId;
const { currentZipCodeAvailabilityData } = useAvailabilityBigTicket() ?? {};
const { resolvedAdditionalFee } = useBigTicketDeliveryMethods();

const isSplitThirdVariantMember = computed(() => isShopTheSetExpEnabled && memberFlags.value?.isSplitThirdVariantFlag);

const showColorSwatches = computed(
  () =>
    (isShopTheSetExpEnabled &&
      ((isMcom && (memberFlags.value?.hasColors || memberFlags.value?.hasColorSwatchesFlag)) || (isBcom && memberFlags.value?.hasColorsFlag))) ||
    (isMcom ? hasColorSwatchesFlag : hasColorsFlag || isLatchKey),
);

const { colorProps } = useColor();

const { maxQuantity, quantity, minQuantity } = useQuantity();
</script>

<style scoped lang="scss">
.product-traits-wrapper {
  display: flex;
  flex-direction: column;
}

:deep() {
  div.select-container {
    margin-bottom: 0;
  }
}
</style>

<style lang="scss">
@import '~settingSCSS';

.product-traits-wrapper {
  .badges {
    > div {
      &:not(:last-child) {
        &:not(:empty) {
          @include margin-bottom-xxs;
        }
      }

      &:last-child {
        &:not(:empty) {
          @include margin-bottom-s;
        }
      }
    }
  }
}
</style>
